import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { ApiError } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../components/ui/Dialog";

export type Translate = ReturnType<typeof useAppPreferences>["t"];

export function errorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.detail;
  return fallback;
}

/**
 * Load data for a page. A response that arrives after the inputs changed (or
 * after unmount) is ignored, so a slow request never overwrites a newer view.
 */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]) {
  const generation = useRef(0);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useAppPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);

  const reload = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const value = await run();
      if (current !== generation.current) return;
      setData(value);
    } catch (loadError) {
      if (current !== generation.current) return;
      setError(errorMessage(loadError, t("This page could not be loaded. Check your connection and try again.")));
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [run, t]);

  useEffect(() => {
    void reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);

  return { data, error, loading, reload, setData };
}

export function PageHeader({
  actions,
  description,
  kicker,
  title,
}: {
  actions?: ReactNode;
  description?: ReactNode;
  kicker?: ReactNode;
  title: ReactNode;
}) {
  return (
    <header className="console-page-header">
      <div className="min-w-0">
        {kicker ? <p className="ops-kicker">{kicker}</p> : null}
        <h1 className="console-page-title">{title}</h1>
        {description ? <p className="console-page-description">{description}</p> : null}
      </div>
      {actions ? <div className="console-page-actions">{actions}</div> : null}
    </header>
  );
}

export function Section({
  actions,
  children,
  description,
  title,
  id,
}: {
  actions?: ReactNode;
  children: ReactNode;
  description?: ReactNode;
  title: ReactNode;
  id?: string;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="ops-surface console-section" id={id}>
      <div className="console-section-header">
        <div className="min-w-0">
          <h2 className="console-section-title" id={headingId}>{title}</h2>
          {description ? <p className="console-muted">{description}</p> : null}
        </div>
        {actions ? <div className="console-section-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

const STATUS_TONES: Record<string, "success" | "warning" | "danger" | "neutral" | "info"> = {
  approved: "success",
  active: "success",
  checked_in: "success",
  confirmed: "info",
  completed: "success",
  issued: "info",
  paid: "success",
  pending: "warning",
  removal_requested: "warning",
  overdue_booking: "warning",
  overdue_session: "danger",
  pending_booking: "warning",
  suspended: "danger",
  rejected: "danger",
  no_show: "danger",
  expired: "neutral",
  removed: "neutral",
  inactive: "neutral",
  void: "neutral",
  cancelled: "neutral",
  overdue: "danger",
  reserved: "warning",
  released: "neutral",
  consumed: "success",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Approved",
  active: "Active",
  checked_in: "Checked in",
  confirmed: "Confirmed",
  completed: "Completed",
  issued: "Issued",
  paid: "Paid",
  pending: "Pending",
  removal_requested: "Removal requested",
  suspended: "Suspended",
  rejected: "Rejected",
  removed: "Removed",
  inactive: "Inactive",
  void: "Void",
  overdue: "Overdue",
  dismissed: "Dismissed",
  cancelled: "Cancelled",
  no_show: "No-show",
  expired: "Expired",
  reserved: "Reserved",
  released: "Released",
  consumed: "Consumed",
};

export function statusLabel(status: string, t: Translate) {
  return t(STATUS_LABELS[status] ?? status.replaceAll("_", " "));
}

export function StatusChip({ status }: { status: string }) {
  const { t } = useAppPreferences();
  return (
    <span className="ops-chip console-status" data-tone={STATUS_TONES[status] ?? "neutral"}>
      {statusLabel(status, t)}
    </span>
  );
}

export function LoadState({
  children,
  empty,
  error,
  isEmpty,
  loading,
  onRetry,
}: {
  children: ReactNode;
  empty?: ReactNode;
  error: string | null;
  isEmpty?: boolean;
  loading: boolean;
  onRetry: () => void;
}) {
  const { t } = useAppPreferences();
  if (error) {
    return (
      <div className="ops-banner" data-tone="danger" role="alert">
        <span className="min-w-0 flex-1">{error}</span>
        <button className="ops-button" data-size="sm" data-variant="secondary" onClick={onRetry} type="button">
          {t("Retry")}
        </button>
      </div>
    );
  }
  if (loading && isEmpty !== false) {
    return <p className="console-muted" role="status">{t("Loading...")}</p>;
  }
  if (isEmpty) return <div className="ops-empty console-empty">{empty ?? t("Nothing here yet.")}</div>;
  return <>{children}</>;
}

export function Notice({ tone, message, onDismiss }: { tone: "success" | "danger"; message: string | null; onDismiss: () => void }) {
  const { t } = useAppPreferences();
  if (!message) return null;
  return (
    <div className="ops-banner" data-tone={tone} role={tone === "danger" ? "alert" : "status"}>
      <span className="min-w-0 flex-1">{message}</span>
      <button aria-label={t("Dismiss message")} className="ops-button shrink-0" data-size="sm" data-variant="secondary" onClick={onDismiss} type="button">×</button>
    </div>
  );
}

export function useNotices() {
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const banners = (
    <>
      <Notice message={notice} onDismiss={() => setNotice(null)} tone="success" />
      <Notice message={error} onDismiss={() => setError(null)} tone="danger" />
    </>
  );
  return { banners, setError, setNotice };
}

export function Facts({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="console-facts">
      {items.map(([label, value], index) => (
        <div className="console-fact" key={index}>
          <dt>{label}</dt>
          <dd>{value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function RecordLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link className="console-link" to={to}>
      {children}
    </Link>
  );
}

export function useFormatters() {
  const { resolvedLanguage } = useAppPreferences();
  const locale = resolvedLanguage === "ar" ? "ar-EG" : "en-GB";
  const dateTime = (value: string | null | undefined) =>
    value ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
  const dateOnly = (value: string | null | undefined) =>
    value ? new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone: "UTC" }).format(new Date(`${value.slice(0, 10)}T00:00:00Z`)) : "—";
  const money = (value: string | number | null | undefined, currency = "EGP") =>
    new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(value ?? 0));
  const age = (value: string | null | undefined) => {
    if (!value) return null;
    const hours = Math.max(0, (Date.now() - new Date(value).getTime()) / 3_600_000);
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (hours < 1) return formatter.format(-Math.round(hours * 60), "minute");
    if (hours < 48) return formatter.format(-Math.round(hours), "hour");
    return formatter.format(-Math.round(hours / 24), "day");
  };
  return { age, dateOnly, dateTime, money };
}

/** Month picker value ("2026-09") <-> API period bounds. */
export function monthBounds(month: string) {
  const [year = 1970, monthNumber = 1] = month.split("-").map(Number);
  const start = new Date(Date.UTC(year, monthNumber - 1, 1));
  const end = new Date(Date.UTC(year, monthNumber, 1));
  const iso = (value: Date) => value.toISOString().slice(0, 10);
  return { period_start: iso(start), period_end: iso(end) };
}

export function currentMonth(offset = 0) {
  const now = new Date();
  const value = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  return value.toISOString().slice(0, 7);
}

export interface ReasonRequest {
  title: string;
  description: string;
  confirmLabel: string;
  fieldLabel: string;
  minLength: number;
  tone?: "danger" | "primary";
}

/** A confirmation that requires a written reason (kept in the audit log). */
export function useReasonPrompt() {
  const { t } = useAppPreferences();
  const [request, setRequest] = useState<(ReasonRequest & { resolve: (value: string | null) => void }) | null>(null);
  const [reason, setReason] = useState("");
  const fieldId = useId();

  const ask = useCallback(
    (next: ReasonRequest) =>
      new Promise<string | null>((resolve) => {
        setReason("");
        setRequest({ ...next, resolve });
      }),
    [],
  );

  const close = (value: string | null) => {
    request?.resolve(value);
    setRequest(null);
  };

  const trimmed = reason.trim();
  const dialog = (
    <Dialog open={request !== null} onOpenChange={(open) => { if (!open) close(null); }}>
      {request ? (
        <DialogContent className="console-dialog">
          <DialogTitle>{request.title}</DialogTitle>
          <DialogDescription>{request.description}</DialogDescription>
          <form
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (trimmed.length >= request.minLength) close(trimmed);
            }}
          >
            <label className="ops-field" htmlFor={fieldId}>
              <span className="ops-label">{request.fieldLabel}</span>
              <textarea
                autoFocus
                className="ops-textarea"
                id={fieldId}
                maxLength={1000}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                value={reason}
              />
              <span className="console-muted">{t("At least {count} characters.", { count: request.minLength })}</span>
            </label>
            <div className="console-dialog-actions">
              <button className="ops-button" data-variant="secondary" onClick={() => close(null)} type="button">
                {t("Cancel")}
              </button>
              <button
                className="ops-button"
                data-variant={request.tone === "danger" ? "danger" : "primary"}
                disabled={trimmed.length < request.minLength}
                type="submit"
              >
                {request.confirmLabel}
              </button>
            </div>
          </form>
        </DialogContent>
      ) : null}
    </Dialog>
  );
  return { ask, dialog };
}
